import React, {useState} from 'react';

// components
import {LoadingView} from "../../../../designSystem/components/LoadingView";
import {ActionButton} from "../../../../designSystem/components/ActionButton";
import {SearchBar} from "../../../../designSystem/components/SearchBar";
import {OrganizationTable} from "../OrganizationTable";
import {Dialog} from "../../../../designSystem/components/Dialog";
import {GroupButtons} from "../GroupButtons";
import {StandardLayout} from "../../../../components/StandardLayout";
import {TotalDisplay} from "../../../../components/PaginationBar";

import {ORGANIZATION_MODAL_CONTENT, OrganizationModalContent} from "../OrganizationModalContent";

import {useExportOrganizationManagement} from "../../hooks/useExportOrganizationManagement";
import {useOrganizations} from "../../hooks/useOrganizations";
import {useOrganizationMutations} from "../../hooks/useOrganizationMutations";
import {useOrganizationFilters} from "../../hooks/useOrganizationFilters";
import {useOrganizationListView} from "../../hooks/useOrganizationListView";

const DEFAULT_USER = {name: '', group: []}
interface OrganizationManagementPageProps {
  userRole: string;
  userOrganizationGroup: string[];
  userToken: string;
}

export const OrganizationManagementFeature = ({userRole, userOrganizationGroup, userToken}: OrganizationManagementPageProps) => {
  const [currentOrganization, setCurrentOrganization] = useState(DEFAULT_USER);
  const [editingOrganization, setEditingOrganization] = useState(null);

  const [dialogContent, setDialogContent] = useState<ORGANIZATION_MODAL_CONTENT | ''>('');
  const [deletingOrgId, setDeletingOrgId] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'success' | 'fail'>('success');
  const {organizations, isLoading, fetchOrganizations} = useOrganizations(userToken);
  const {
    searchTerm,
    setSearchTerm,
    selectedGroup,
    sortColumn,
    sortOrder,
    handleSort,
    handleChangeSelectedGroup,
  } = useOrganizationFilters();

  // trigger when the create organization button is clicked
  const onClickCreateOrganization = () => {
    setEditingOrganization(null);
    setCurrentOrganization(DEFAULT_USER);
    setDialogContent(ORGANIZATION_MODAL_CONTENT.ORGANIZATION_FORM);
  }

  // trigger when the edit organization button is clicked
  const onClickEditOrganization = (org) => {
    setEditingOrganization(org);
    setCurrentOrganization(org);
    setDialogContent(ORGANIZATION_MODAL_CONTENT.ORGANIZATION_FORM);
  }

  // trigger when the delete organization button is clicked
  const onClickDeleteOrganization = (org) => {
    setDialogContent(ORGANIZATION_MODAL_CONTENT.CONFIRM_DELETE);
    setDeletingOrgId(org.id);
  }

  // trigger when the dialog backdrop is clicked
  const onDialogBackdropClick = () => {
    if (
      dialogContent === ORGANIZATION_MODAL_CONTENT.ORGANIZATION_FORM ||
      dialogContent === ORGANIZATION_MODAL_CONTENT.CONFIRM_DELETE
    ) {
      setDialogContent('')
    }
  }

  // handle to close the dialog
  const handleCloseDialog = () => setDialogContent('');

  const {handleSaveOrganization, handleDeleteOrganization} = useOrganizationMutations({
    userToken,
    editingOrganization,
    currentOrganization,
    setDialogContent,
    setMessage,
    setMessageType,
    setEditingOrganization,
    setCurrentOrganization,
    setDeletingOrgId,
    fetchOrganizations,
  });

  // handle when the organization name input box is changed
  const handleChangeOrganizationName = (value: string) => {
    setCurrentOrganization({...currentOrganization, name: value})
  }

  // handle when the organization group name selector is changed
  const handleChangeOrganizationGroupName = (groupName: string) => {
    setCurrentOrganization(prev => ({
      ...prev,
      group: prev.group.includes(groupName)
        ? prev.group.filter(g => g !== groupName)
        : [...prev.group, groupName]
    }))
  }

  const {filteredOrganizations, sortedOrganizations} = useOrganizationListView({
    organizations,
    searchTerm,
    selectedGroup,
    sortColumn,
    sortOrder,
  });

  const { exportData } = useExportOrganizationManagement({ data: sortedOrganizations });

  // Rendering
  if (isLoading || !userToken)
    return <LoadingView/>

  return (
    <React.Fragment>
      <StandardLayout
        headerTitle="業務組別管理"
        headerActionLabel="新增業務組別"
        headerActionOnClick={onClickCreateOrganization}
        body={
          <div className="w-full m-auto flex flex-col gap-4">
            <SearchBar
              placeholder="搜尋業務組別名稱" value={searchTerm}
              onChangeValue={(value: string) => setSearchTerm(value)}
            />

            {/* group category filter buttons */}
            <GroupButtons
              userRole={userRole}
              userOrganizationGroup={userOrganizationGroup}
              selectedGroup={selectedGroup}
              onChangeSelectedGroup={handleChangeSelectedGroup}
            />

            <div className="flex flex-row justify-between items-center">
              <TotalDisplay numberOfData={filteredOrganizations?.length}/>
              <ActionButton
                label="匯出資料"
                onClick={exportData}
                iconName="fa-file-export"
                variant="outline"
              />
            </div>

            {/* the organization table */}
            {filteredOrganizations?.length > 0
              ? <OrganizationTable
                sortColumn={sortColumn}
                sortOrder={sortOrder}
                organizations={sortedOrganizations}
                handleSort={handleSort}
                handleEdit={(org) => onClickEditOrganization(org)}
                handleDelete={(org) => onClickDeleteOrganization(org)}
              />
              : null
            }
          </div>
        }
      />
      <Dialog
        isOpen={dialogContent !== ''}
        onBackdropClick={onDialogBackdropClick}
      >
        {OrganizationModalContent({
          message,
          messageType,
          editingOrganization,
          userRole,
          userOrganizationGroup,
          currentOrganization,
          deletingOrganizationId: deletingOrgId,
          handleCloseDialog,
          handleDeleteOrganization,
          handleChangeOrganizationName,
          handleChangeOrganizationGroupName,
          handleSaveOrganization
        })[dialogContent] || <></>}
      </Dialog>
    </React.Fragment>
  );
};
